import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, ChevronLeft, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { P } from '@/lib/rbac/permissions.generated';
import { usePermission } from '@/components/rbac/use-permission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Kalender (RBAC-SPEC D.2, Entscheidung 11). Was ein Eintrag enthält, entscheidet der
// Server: maskierte Einträge kommen nur mit Zeitraum und „Nicht verfügbar“ an und
// werden hier genau so gezeigt wie alle anderen — ohne Typ, ohne eigenes Styling.

interface CalendarEntry {
  id: string;
  owner_account_id: string;
  starts_at: string;
  ends_at: string;
  title: string;
  description?: string | null;
  /** Fehlt bei maskierten Einträgen — die lassen sich hier auch nicht ändern. */
  kind?: string;
}

const KINDS = [
  { value: 'normal', label: 'Termin' },
  { value: 'event', label: 'Event' },
  { value: 'urlaub', label: 'Urlaub' },
  { value: 'krankmeldung', label: 'Krankmeldung' },
  { value: 'privat', label: 'Privater Termin' },
] as const;

const DAYS = 28;

const startOfToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const dayLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });

const timeLabel = (iso: string): string =>
  new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

// ISO-Zeitpunkt → Wert für <input type="datetime-local"> in Ortszeit.
const toLocalInput = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY_FORM = { kind: 'normal', title: '', starts_at: '', ends_at: '' };

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** Die Tage des Monatsrasters: volle Wochen von Montag bis Sonntag (T-0259). */
const monatsRaster = (monat: Date): Date[] => {
  const erster = new Date(monat.getFullYear(), monat.getMonth(), 1);
  const start = new Date(erster);
  start.setDate(erster.getDate() - ((erster.getDay() + 6) % 7));
  const letzter = new Date(monat.getFullYear(), monat.getMonth() + 1, 0);
  const ende = new Date(letzter);
  ende.setDate(letzter.getDate() + ((7 - letzter.getDay()) % 7));
  const tage: Date[] = [];
  for (const d = new Date(start); d <= ende; d.setDate(d.getDate() + 1)) tage.push(new Date(d));
  return tage;
};

const gleicherTag = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const Kalender = () => {
  const canCreate = usePermission(P.arbeit.kalendereintraege.eintraege_erstellen);
  const canEdit = usePermission(P.arbeit.kalendereintraege.eigene_eintraege_bearbeiten);
  const canDelete = usePermission(P.arbeit.kalendereintraege.eigene_eintraege_loeschen);
  // Bearbeiten und Löschen gab es bisher nicht (T-0107); ob der Eintrag einem
  // gehört oder man ihn verwalten darf, prüft der Server.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // Neben der Liste der nächsten vier Wochen eine Monatsübersicht (T-0259).
  const [ansicht, setAnsicht] = useState<'liste' | 'monat'>('liste');
  const [monat, setMonat] = useState(() => {
    const heute = startOfToday();
    return new Date(heute.getFullYear(), heute.getMonth(), 1);
  });
  const raster = useMemo(() => monatsRaster(monat), [monat]);

  const range = useMemo(() => {
    if (ansicht === 'monat') {
      const bis = new Date(raster[raster.length - 1]);
      bis.setDate(bis.getDate() + 1);
      return { from: raster[0].toISOString(), to: bis.toISOString() };
    }
    const from = startOfToday();
    const to = new Date(from.getTime() + DAYS * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [ansicht, raster]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<CalendarEntry[]>(
        `/api/arbeit/kalender?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      );
      setEntries(Array.isArray(data) ? data : []);
      setFailed(false);
    } catch {
      setEntries([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  const byDay = useMemo(() => {
    const groups = new Map<string, CalendarEntry[]>();
    for (const entry of entries) {
      const key = new Date(entry.starts_at).toDateString();
      groups.set(key, [...(groups.get(key) ?? []), entry]);
    }
    return [...groups.values()];
  }, [entries]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim() || !form.starts_at || !form.ends_at) {
      toast.error('Bitte Titel, Beginn und Ende angeben.');
      return;
    }
    // Der Server lehnt das mit 422 ab; vorher stand nur „konnte nicht angelegt werden“
    // da, ohne Grund (T-0255).
    if (new Date(form.ends_at).getTime() < new Date(form.starts_at).getTime()) {
      toast.error('Das Ende liegt vor dem Beginn.');
      return;
    }
    setSaving(true);
    const body = {
      kind: form.kind,
      title: form.title.trim(),
      starts_at: new Date(form.starts_at).toISOString(),
      ends_at: new Date(form.ends_at).toISOString(),
    };
    try {
      if (editingId) {
        await api.patch(`/api/arbeit/kalender/${editingId}`, body);
        toast.success('Eintrag gespeichert.');
      } else {
        await api.post('/api/arbeit/kalender', body);
        toast.success('Eintrag angelegt.');
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      await load();
    } catch (e: unknown) {
      const grund = e instanceof Error && e.message ? ` ${e.message}` : '';
      toast.error(`${editingId ? 'Der Eintrag konnte nicht gespeichert werden.' : 'Der Eintrag konnte nicht angelegt werden.'}${grund}`);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry: CalendarEntry) => {
    setEditingId(entry.id);
    setForm({
      kind: entry.kind ?? 'normal',
      title: entry.title,
      starts_at: toLocalInput(entry.starts_at),
      ends_at: toLocalInput(entry.ends_at),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (entry: CalendarEntry) => {
    const grund = window.prompt(`„${entry.title}“ löschen? Bitte kurz den Grund angeben:`, '');
    if (grund === null) return;
    if (!grund.trim()) {
      toast.error('Ohne Grund wird nichts gelöscht.');
      return;
    }
    try {
      await api.delete(`/api/arbeit/kalender/${entry.id}`, { headers: { 'X-Sidigo-Reason': grund.trim() } });
      toast.success('Eintrag gelöscht.');
      if (editingId === entry.id) {
        setEditingId(null);
        setForm(EMPTY_FORM);
      }
      await load();
    } catch {
      toast.error('Der Eintrag konnte nicht gelöscht werden.');
    }
  };

  const tagVorbelegen = (tag: Date) => {
    if (!canCreate || editingId) return;
    const beginn = new Date(tag.getFullYear(), tag.getMonth(), tag.getDate(), 9, 0);
    setForm((f) => ({
      ...f,
      starts_at: toLocalInput(beginn.toISOString()),
      ends_at: toLocalInput(new Date(beginn.getTime() + 3_600_000).toISOString()),
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const monatsName = monat.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const heute = startOfToday();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Kalender</h1>
          <p className="text-sm text-muted-foreground">
            {ansicht === 'liste' ? 'Einträge der nächsten vier Wochen.' : 'Alle Einträge des Monats.'}
          </p>
        </div>
        <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Ansicht">
          {(['liste', 'monat'] as const).map((wert) => (
            <Button
              key={wert}
              type="button"
              size="sm"
              variant={ansicht === wert ? 'default' : 'ghost'}
              aria-pressed={ansicht === wert}
              onClick={() => setAnsicht(wert)}
            >
              {wert === 'liste' ? 'Liste' : 'Monat'}
            </Button>
          ))}
        </div>
      </div>

      {(canCreate || editingId) && (
        <form onSubmit={submit} className="grid gap-3 rounded-lg border border-border/70 p-4 md:grid-cols-5">
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="kalender-titel">Titel</Label>
            <Input
              id="kalender-titel"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label>Art</Label>
            <Select value={form.kind} onValueChange={(kind) => setForm((f) => ({ ...f, kind }))}>
              <SelectTrigger aria-label="Art">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KINDS.map((kind) => (
                  <SelectItem key={kind.value} value={kind.value}>
                    {kind.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="kalender-beginn">Beginn</Label>
            <Input
              id="kalender-beginn"
              type="datetime-local"
              value={form.starts_at}
              onChange={(e) => {
                const beginn = e.target.value;
                setForm((f) => {
                  // Ende eine Stunde später vorbelegen, wenn es fehlt oder davor liegt.
                  const ende = f.ends_at && new Date(f.ends_at) >= new Date(beginn)
                    ? f.ends_at
                    : beginn ? toLocalInput(new Date(new Date(beginn).getTime() + 3_600_000).toISOString()) : f.ends_at;
                  return { ...f, starts_at: beginn, ends_at: ende };
                });
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="kalender-ende">Ende</Label>
            <Input
              id="kalender-ende"
              type="datetime-local"
              value={form.ends_at}
              onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
            />
          </div>
          <div className="flex gap-2 md:col-span-5">
            <Button type="submit" disabled={saving}>
              {editingId ? 'Änderungen speichern' : 'Eintrag anlegen'}
            </Button>
            {editingId && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                }}
              >
                Abbrechen
              </Button>
            )}
          </div>
        </form>
      )}

      {ansicht === 'monat' ? (
        <section className="space-y-3" aria-label="Monatsübersicht">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="icon"
              variant="outline"
              aria-label="Voriger Monat"
              onClick={() => setMonat((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="min-w-[10rem] text-center font-semibold">{monatsName}</h2>
            <Button
              type="button"
              size="icon"
              variant="outline"
              aria-label="Nächster Monat"
              onClick={() => setMonat((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          {failed ? (
            <EmptyState message="Die Einträge konnten nicht geladen werden." />
          ) : (
            <div className="grid grid-cols-7 overflow-hidden rounded-lg border text-sm">
              {WOCHENTAGE.map((tag) => (
                <div key={tag} className="border-b bg-muted/40 px-2 py-1 text-xs font-semibold text-muted-foreground">
                  {tag}
                </div>
              ))}
              {raster.map((tag) => {
                const eintraege = entries.filter((e) => gleicherTag(new Date(e.starts_at), tag));
                const imMonat = tag.getMonth() === monat.getMonth();
                return (
                  <div
                    key={tag.toISOString()}
                    data-tag={toLocalInput(tag.toISOString()).slice(0, 10)}
                    className={`min-h-[6rem] border-b border-r p-1 ${imMonat ? '' : 'bg-muted/20 text-muted-foreground'}`}
                  >
                    <button
                      type="button"
                      className={`mb-1 rounded px-1 text-xs ${gleicherTag(tag, heute) ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => tagVorbelegen(tag)}
                      aria-label={`${tag.toLocaleDateString('de-DE')} – Eintrag an diesem Tag anlegen`}
                      disabled={!canCreate}
                    >
                      {tag.getDate()}
                    </button>
                    <ul className="space-y-0.5">
                      {eintraege.slice(0, 3).map((entry) => (
                        <li key={entry.id}>
                          <button
                            type="button"
                            className="w-full truncate rounded bg-primary/10 px-1 text-left text-xs hover:bg-primary/20 disabled:cursor-default"
                            title={`${timeLabel(entry.starts_at)}–${timeLabel(entry.ends_at)} ${entry.title}`}
                            disabled={!entry.kind || !canEdit}
                            onClick={() => startEdit(entry)}
                          >
                            {timeLabel(entry.starts_at)} {entry.title}
                          </button>
                        </li>
                      ))}
                      {eintraege.length > 3 ? (
                        <li className="px-1 text-xs text-muted-foreground">+{eintraege.length - 3} weitere</li>
                      ) : null}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : loading ? (
        <LoadingState message="Einträge werden geladen" />
      ) : failed ? (
        <EmptyState message="Die Einträge konnten nicht geladen werden." />
      ) : byDay.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="h-10 w-10" />}
          message="In diesem Zeitraum gibt es keine Einträge, die Sie sehen können."
        />
      ) : (
        <div className="space-y-4">
          {byDay.map((day) => (
            <section key={day[0].starts_at} className="space-y-2">
              <h2 className="text-sm font-semibold text-muted-foreground">{dayLabel(day[0].starts_at)}</h2>
              <ul className="space-y-1">
                {day.map((entry) => (
                  <li key={entry.id} className="rounded-md border border-border/70 px-3 py-2 text-sm">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <span className="mr-3 font-mono text-muted-foreground">
                          {timeLabel(entry.starts_at)}–{timeLabel(entry.ends_at)}
                        </span>
                        <span>{entry.title}</span>
                      </div>
                      {entry.kind && canEdit && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          aria-label="Bearbeiten"
                          onClick={() => startEdit(entry)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {entry.kind && canDelete && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          aria-label="Löschen"
                          onClick={() => void remove(entry)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {entry.description ? (
                      <p className="mt-1 text-xs text-muted-foreground">{entry.description}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default Kalender;
